import {
  Box,
  Checkbox,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {ISelectItem} from '../../../shared/types';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import {ReactComponent as AddFieldSortIcon} from '../../../assets/images/add-fields-sort.svg';
import {ReactComponent as CheckedIcon} from '../../../../src/assets/images/icon-checked.svg';
import {ReactComponent as UnCheckedIcon} from '../../../../src/assets/images/icon-unchecked.svg';
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import {
  getSavedSearchDeleteMessage,
  savedSearchColumns,
  savedSearchMoreMenu,
  statusDropdown,
} from '../SavedSearchUtils';
import './SavedSearchDataGrid.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  useDeleteSavedSearch,
  useGetShareGroups,
  useRenameSavedSearch,
  useSavedSearchList,
  useShareSavedSearch,
} from '../../../hooks/saved-search/useSavedSearch';
import {ISavedSearchResponse} from '../../../api/types/savedsearch.types';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrModal from '../../common/arr-modal/ArrModal';
import {ToastType} from '../../../shared/constants';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import SavedSearchDataGridMoveFlyout from './saved-search-data-grid-move-flyout/SavedSearchDataGridMoveFlyout';
import {EditSavedSearchNewRequest} from '../../../api/types/searchType.type';
import {SavedSearchStatus} from '../../../api/types/sessionSearch.type';
import {getEditSavedSearchNew} from '../../../api/endpoints/searchApi';
import {useNavigate} from 'react-router-dom';
import {
  getSavedSearchRootCategoryName,
  transformSavedSearchToSessionSearch,
} from '../../saved-search/saved-search-utils';
import {useReloadSearchResult} from '../../../hooks/session-search/useSessionSearch';
import {toSearchResult} from '../../session-search/utilities/utilities';
import {SessionSearch} from '../../../components/session-search/types/SessionSearch';
import {useSavedSearchTreeView} from '../../../hooks/saved-search/savedSearch';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {trackSavedSearchColumnAdded, trackSavedSearchColumnRemoved, trackSavedSearchFilterApplied, trackSavedSearchMenuAction, trackPaginationClick, trackSavedSearchResultsSorted} from '../../../utils/analyticsUtils';

const ColumnMenuItem: React.FC<CustomMenuItem> = React.memo(
  ({id, label, checked, disabled, onToggle}) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (disabled) return;
      onToggle?.();
    };
    return (
      <MenuItem
        dense
        disableRipple
        disabled={disabled}
        onClick={handleClick}
        role="menuitemcheckbox"
        aria-checked={checked}
        aria-disabled={disabled}
        data-testid={`saved-search-data-grid-menu-item-${id}`}
      >
        <ListItemIcon>
          <Checkbox
            data-testid={`saved-search-grid-column-menu-checkbox-${id}`}
            edge="start"
            checked={checked}
            tabIndex={-1}
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            size="small"
            icon={<UnCheckedIcon />}
            checkedIcon={<CheckedIcon />}
            disabled={disabled}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
    );
  }
);

const NoRowsOverlay = ({isFiltered}: {isFiltered: boolean}) => (
  <Box className="no-project-data-found">
    <NoDataIcon />
    <Typography
      className="typo-text headline3_size no-saved-search-text"
      data-testid={`saved-search-data-grid-message-no-data`}
    >
      {isFiltered
        ? t('common.messageForNoGridData')
        : t('savedSearch.savedSearchFilterGrid.grid.messageNoData')}
    </Typography>
  </Box>
);

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ROW_HEIGHT = 36;

interface SavedSearchDataGridProps {
  selectedItem: string;
  onRowSelect?: (row: ISavedSearchResponse) => void;
  handleSavedSearchAll?: (rows: ISavedSearchResponse[]) => void;
  refetchList?: boolean;
  setRefetchList?: (value: boolean) => void;
}

export interface SavedSearchTreeNode {
  id: string;
  parent?: string;
  label?: string;
  text?: string;
  textWithoutCount?: string;
  children?: SavedSearchTreeNode[];
}

const SavedSearchDataGrid: React.FC<SavedSearchDataGridProps> = ({
  selectedItem,
  onRowSelect,
  handleSavedSearchAll,
  refetchList,
  setRefetchList,
}) => {
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const menuRef2 = useRef<ArrMenuHandle | null>(null);
  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  const roleId = useSelector(
    (state: RootState) => state.authenticatedUser.user.roleId
  );
  const [searchName, setSearchName] = useState<string>('');
  const [searchID, setSearchID] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<ISelectItem | null>(
    null
  );
  const [rows, setRows] = useState<ISavedSearchResponse[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    searchCode: '',
    searchName: '',
    searchID: 0,
  });
  const isFiltered =
    appliedFilters.searchName !== '' ||
    appliedFilters.searchID !== 0 ||
    appliedFilters.searchCode !== '';
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [shareWith, setShareWith] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [renameId, setRenameId] = useState<number>(0);
  const [renameValue, setRenameValue] = useState<string>('');
  const [openMoveFlyout, setOpenMoveFlyout] = useState(false);
  const navigate = useNavigate();
  const {mutateAsync: loadsearchResult} = useReloadSearchResult();
  const {data: treeData} = useSavedSearchTreeView() as {
    data?: SavedSearchTreeNode[];
  };
  const selectedNode = treeData?.find((node) => node.id === selectedItem);
  const parentId =
    selectedNode?.parent === '-1'
      ? (selectedNode?.parent ?? selectedItem)
      : selectedItem;
  const [paginationSelectedItem, setPaginationSelectedItem] =
    useState(selectedItem);
  const effectivePageNumber =
    paginationSelectedItem === selectedItem ? pageNumber : 1;

  useEffect(() => {
    setSelectedRowId(null);
    setPageNumber(1);
    setPaginationSelectedItem(selectedItem);
  }, [selectedItem]);

  const baseColumns = useMemo<GridColDef[]>(() => {
    return savedSearchColumns().map((col) => ({
      ...col,
      minWidth: col.minWidth ?? 96,
    }));
  }, []);

  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>(() =>
    baseColumns.reduce((acc, c) => ({...acc, [c.field]: true}), {})
  );

  const handleManageColumnsClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef2.current?.open(e.currentTarget as HTMLElement);
  };

  type ColumnDef = ReturnType<typeof savedSearchColumns>[number];
  type FieldKey = ColumnDef['field'];

  const visibleCount = useMemo(
    () => Object.values(visibility).filter(Boolean).length,
    [visibility]
  );

  const menuClickedItem = useRef<ISavedSearchResponse>(null);

  const handleMoreClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, row: ISavedSearchResponse) => {
      e.stopPropagation();
      menuRef1.current?.open(e.currentTarget as HTMLElement);
      menuClickedItem.current = row;
    },
    []
  );

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };

  const renderActionsCell = useCallback(
    (params: GridRenderCellParams) => {
      const row = params.row as ISavedSearchResponse;
      const handleRadioChange = (val: string) => {
        setSelectedRowId(Number(val));
        onRowSelect?.(row);
      };
      return (
        <Box className="saved-search-grid-action">
          <ArrIconButton
            onClick={(e) => {
              handleMoreClick(e, row);
            }}
            icon={<MoreIcon />}
            ariaLabel={t('common.moreOptions')}
            shape="none"
            className="user-grid-icon-button"
            testId="saved-search-data-grid-more-options-icon-button"
          />
          <ArrRadio
            name="saved-search-radio"
            value={selectedRowId ? selectedRowId.toString() : ''}
            onChange={handleRadioChange}
            data={[
              {
                value: row.searchID.toString(),
                label: '',
              },
            ]}
            size="xs"
            className="row-radio"
            dataTestId={`saved-search-radio-${selectedRowId}`}
          />
        </Box>
      );
    },
    [selectedRowId, handleMoreClick, onRowSelect]
  );

  const sortBy = sortModel[0]?.field ?? '';
  const sortingOrder = sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

  const payload = {
    ...Object.fromEntries(
      Object.entries(appliedFilters).filter(([_, value]) => value !== '')
    ),
    searchGroupId: Number(selectedItem),
    sortBy: sortBy,
    sortingOrder: sortingOrder,
    pageNumber: effectivePageNumber,
    pageSize: itemsPerPage,
  };

  const updatedColumns = useMemo(
    () => [
      {
        field: '',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        headerClassName: 'typo-text label2_size sticky-col left-col-header',
        cellClassName: 'action-cell sticky-col left-col-cell',
        renderCell: renderActionsCell,
      },
      ...baseColumns,
    ],
    [baseColumns, renderActionsCell]
  );

  const {
    data: savedSearchList,
    isLoading: isSavedSearchListLoading,
    refetch: updateSavedSearchList,
  } = useSavedSearchList(payload, !!selectedItem);

  const {mutate: renameSavedSearch} = useRenameSavedSearch();
  const {data: sharedSecurityGroups} = useGetShareGroups(openShareModal);
  const {mutate: shareSearch} = useShareSavedSearch();
  const {mutate: deleteSearch} = useDeleteSavedSearch();

  useEffect(() => {
    setRows(savedSearchList?.data ?? []);
    handleSavedSearchAll?.(savedSearchList?.data ?? []);
  }, [savedSearchList]);

  useEffect(() => {
    if (refetchList) {
      updateSavedSearchList();
      setRefetchList?.(false);
    }
  }, [refetchList, setRefetchList, updateSavedSearchList]);

  const toggle = useCallback(
    (field: FieldKey) => {
      setVisibility((prev) => {
        const currentlyVisible = Boolean(prev[field]);
        const currentlyVisibleCount =
          Object.values(prev).filter(Boolean).length;
        if (currentlyVisible && currentlyVisibleCount === 1) {
          return prev;
        }
        return {...prev, [field]: !currentlyVisible};
      });

      const currentlyVisible = Boolean(visibility[field]);
      if (currentlyVisible && visibleCount === 1) {
        return;
      }

      const columnName = baseColumns.find(
        (column) => column.field === field
      )?.headerName;
      if (currentlyVisible) {
        trackSavedSearchColumnRemoved(trackGAEvent, {columnName});
      } else {
        trackSavedSearchColumnAdded(trackGAEvent, {columnName});
      }
    },
    [setVisibility, visibility, visibleCount, baseColumns, trackGAEvent]
  );

  const items = useMemo(
    () =>
      baseColumns.map((c) => {
        const checked = Boolean(visibility[c.field]);
        const disabled = checked && visibleCount === 1;
        return {
          id: c.field,
          label: c.headerName ?? '',
          checked,
          disabled,
          onToggle: () => toggle(c.field),
        };
      }),
    [baseColumns, visibility, visibleCount, toggle]
  );

  const disableFilters = () => {
    const checkSearchId = searchID === '' ? 0 : Number(searchID);
    const searchCodeCheck =
      selectedStatus === null ? '' : selectedStatus?.id?.toString();
    if (
      appliedFilters.searchName === searchName &&
      searchName === '' &&
      appliedFilters.searchID === checkSearchId &&
      checkSearchId === 0 &&
      appliedFilters.searchCode === searchCodeCheck &&
      searchCodeCheck === ''
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleApply = () => {
    trackSavedSearchFilterApplied(trackGAEvent, {
      hasStatus: selectedStatus != null,
      hasSearchName: Boolean(searchName),
      hasSearchId: Boolean(searchID),
      statusValue: selectedStatus?.name,
    });
    setAppliedFilters({
      searchName: searchName,
      searchID: searchID === '' ? 0 : Number(searchID),
      searchCode: selectedStatus?.id?.toString() ?? '',
    });
  };

  const handleKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleApply();
    }
  };

  const handleMenuItemClick = useCallback(
    (item: {id: string}) => {
      trackSavedSearchMenuAction(trackGAEvent, {
        actionId: item.id,
        categoryName: getSavedSearchRootCategoryName(
          treeData ?? [],
          selectedItem
        ),
        searchId: menuClickedItem.current?.searchID,
      });

      if (item.id == 'share') {
        setOpenShareModal(true);
      } else if (item.id === 'rename') {
        setRenameId(menuClickedItem.current?.searchID ?? -1);
        setRenameValue(menuClickedItem.current?.searchName ?? '');
      } else if (item.id == 'delete') {
        setOpenDeleteModal(true);
      } else if (item.id == 'move') {
        setOpenMoveFlyout(true);
      } else if (item.id == 'edit') {
        NavigateToSearch();
      }
      menuRef1.current?.close();
    },
    [selectedItem, trackGAEvent, treeData]
  );

  const NavigateToSearch = async () => {
    const payload: EditSavedSearchNewRequest = {
      searchId: menuClickedItem.current?.searchID ?? -1,
      searchTitle: menuClickedItem.current?.searchName ?? '',
      totalCount: Number(menuClickedItem.current?.result) || 0,
      searchStatus: menuClickedItem.current?.status ?? '',
      sessionID: '',
      sessionSearchList: [],
      sessionSecurityGroupId: 1,
    };

    try {
      const data = await getEditSavedSearchNew(payload);

      if (data && data.sessionSearchList && data.sessionSearchList.length > 0) {
        const transformedSearches: SessionSearch[] = [];

        for (const sessionSearchItem of data.sessionSearchList) {
          const transformedSearch =
            transformSavedSearchToSessionSearch(sessionSearchItem);

          if (
            sessionSearchItem.searchStatus === SavedSearchStatus.COMPLETED &&
            sessionSearchItem.searchID
          ) {
            try {
              const cleanedSessionSearchItem = {
                ...sessionSearchItem,
                advancedSearchTypes:
                  sessionSearchItem.advancedSearchTypes?.map((ast: any) => ({
                    ...ast,
                    operator: ast.operator || '',
                    audioOperator: ast.audioOperator || '',
                    audioLanguage: ast.audioLanguage || '',
                    audioFileLocation: ast.audioFileLocation || '',
                    highlightRegex:
                      ast.highlightRegex?.map((hr: any) => ({
                        ...hr,
                        regexOnly: hr.regexOnly || '',
                        regexComplete: hr.regexComplete || '',
                        sourceValue: hr.sourceValue || '',
                      })) || [],
                  })) || [],
              };

              const resultRequest = {
                searchId: sessionSearchItem.searchID,
                resultType: 'ADVANCEDSEARCH',
                isElasticSearch: 0,
                isSavedSearch: false,
                securityGroup: 0,
                sessionSearchList: [cleanedSessionSearchItem],
                sessionSearchId: sessionSearchItem.searchID.toString(),
              };

              const resultData = await loadsearchResult(resultRequest);

              transformedSearch.searchResult = toSearchResult(resultData);
              transformedSearch.lastRuntime = new Date();
              transformedSearch.advancedSearchId = sessionSearchItem.searchID;
              transformedSearch.indexId =
                menuClickedItem?.current?.indexId ?? 0;
            } catch (error) {
              console.error(error);
            }
          } else if (
            sessionSearchItem.searchStatus === SavedSearchStatus.DRAFT &&
            sessionSearchItem.searchID
          ) {
            // Same read-only + Modify Search treatment as COMPLETED, but there
            // are no results to load yet since the search was never run.
            transformedSearch.lastRuntime = new Date();
            transformedSearch.advancedSearchId = sessionSearchItem.searchID;
            transformedSearch.indexId = menuClickedItem?.current?.indexId ?? 0;
          }
          transformedSearches.push(transformedSearch);
        }

        navigate('/Search/Searches', {
          state: {
            loadedSearches: transformedSearches,
            searchStatus: menuClickedItem.current?.status,
          },
        });
      }
    } catch (error) {
      triggerToast(t('savedSearch.editSearch.loadError'), [ToastType.Error]);
      console.error(error);
    }
  };

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleShare = () => {
    const sharedGroup = sharedSecurityGroups?.securityGroups.find(
      (item) => item.toGroupID.toString() == shareWith
    );
    if (!sharedGroup || !menuClickedItem.current) {
      return;
    }
    const sharePayload = {
      searchID: menuClickedItem.current?.searchID,
      toGroupID: sharedGroup?.toGroupID,
      isSearchGroup: false,
      destSecurityGroupID: sharedGroup?.securityGroupID,
      notificationURL: sharedGroup?.notificationURL ?? '',
      emailId: sharedGroup?.emailId ?? '',
      name: sharedGroup?.name,
      shareType: 'SGSHARED',
      originalSearchID: menuClickedItem.current?.searchID,
    };
    shareSearch(sharePayload, {
      onSuccess: (result) => {
        triggerToast(result.successMessage, [ToastType.Success]);
        setOpenShareModal(false);
        updateSavedSearchList();
        setShareWith('');
      },
      onError: () => {
        triggerToast(t('savedSearch.shareModal.shareError'), [ToastType.Error]);
      },
    });
  };

  const handleDelete = () => {
    if (menuClickedItem.current?.searchID) {
      deleteSearch(
        {
          savedSearchId: menuClickedItem.current?.searchID,
          searchGroupID: Number(selectedItem),
          parentSearchGroupID: 0,
          searchGroupLabel: 'string',
          roleID: 0,
          userID: 0,
          securityGroupID: 0,
          projectId: 0,
          searchGroupType: 'string',
          searchGroupSecurityGroupId: 0,
          toolTipRef: 'string',
          securityGroupName: menuClickedItem?.current?.securityGroupName ?? '',
        },
        {
          onSuccess: (result) => {
            triggerToast(result.successMessage, [ToastType.Success]);
            setOpenDeleteModal(false);
            updateSavedSearchList();
          },
          onError: (error: any) => {
            if (error?.response?.status === 500) {
              const errorMessage =
                error?.response?.data?.detail?.split('\n')[0] ||
                t('savedSearch.deleteModal.deleteError');
              const displayErrorMessage =
                getSavedSearchDeleteMessage(errorMessage);
              setOpenDeleteModal(false);
              triggerToast(displayErrorMessage, [ToastType.Error]);
            } else {
              setOpenDeleteModal(false);
              triggerToast(t('savedSearch.deleteModal.deleteError'), [
                ToastType.Error,
              ]);
            }
          },
        }
      );
    }
  };

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const sortField = model[0]?.field;
      if (sortField) {
        trackSavedSearchResultsSorted(trackGAEvent, {
          columnName: baseColumns.find((column) => column.field === sortField)
            ?.headerName,
        });
      }
      setSortModel(model);
    },
    [baseColumns, trackGAEvent]
  );

  const handleColumnVisibilityModelChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    const visible = Object.values(newModel ?? {}).filter(Boolean).length;
    if (visible === 0) {
      return;
    }
    setVisibility(newModel);
  };

  const handleMoveSave = () => {
    updateSavedSearchList();
  };

  const getSavedSearchData = () => {
    if (!menuClickedItem.current) {
      return {
        savedSearchId: 0,
        searchGroupID: 0,
        searchGroupLabel: '',
        searchGroupType: '',
        searchGroupSecurityGroupId: 0,
        toolTipRef: '',
        securityGroupId: 0,
        savedSearches: treeData,
        indexData: {
          indexId: null,
          indexName: '',
          indexStatus: '',
          indexSecurityGroupIds: [],
        },
      };
    }
    return {
      savedSearchId: menuClickedItem.current.searchID,
      searchGroupID: Number(selectedItem),
      searchGroupLabel: menuClickedItem.current.searchName,
      searchGroupType: 'SEARCH',
      searchGroupSecurityGroupId: 0,
      toolTipRef: '',
      securityGroupId: Number(menuClickedItem.current.securityGroupId),
      savedSearches: treeData,
      indexData: {
        indexId: menuClickedItem.current.indexId,
        indexName: menuClickedItem.current.indexName,
        indexStatus: menuClickedItem.current.indexStatus,
        indexSecurityGroupIds: menuClickedItem.current.indexSecurityGroupId,
      },
    };
  };
  // On clear Refresh
  const handleStatusChange = useCallback((value: ISelectItem | null) => {
    setSelectedStatus(value);
    if (value === null) {
      setAppliedFilters((prev) => ({
        ...prev,
        searchCode: '',
      }));
    }
  }, []);

  const handleSearchNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchName(value);
      if (value === '') {
        setAppliedFilters((prev) => ({
          ...prev,
          searchName: '',
        }));
      }
    },
    []
  );

  const handleSearchIDChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^\d*$/.test(value)) {
        setSearchID(value);
        if (value === '') {
          setAppliedFilters((prev) => ({
            ...prev,
            searchID: 0,
          }));
        }
      }
    },
    []
  );

  return (
    <Box className="saved-search-filter-data-grid">
      <Box className="saved-search-filter">
        <Typography
          className="typo-text subhead4_size search-title"
          data-testid={`saved-search-data-grid-title`}
        >
          {t('savedSearch.savedSearchFilterGrid.title')}
        </Typography>
        <Box className="saved-search-filter-panel-controls">
          <Grid className="saved-search-filter-fields">
            <Grid className="saved-search-item-flex">
              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'savedSearch.savedSearchFilterGrid.selectStatus'
                )}
                dropdownItems={statusDropdown}
                selectedValues={selectedStatus}
                onSelectionChange={handleStatusChange}
                sizeVariant="medium"
                clearSelection
                dataTestId="asd-selectStatus"
              />
            </Grid>
            <Grid className="filter-item">
              <Typography
                className="typo-text body3_size"
                data-testid={`saved-search-data-grid-search-name-label`}
              >
                {t('savedSearch.savedSearchFilterGrid.searchNameLabel')}
              </Typography>
              <ArrTextField
                state="enabled"
                sizeVariant="medium"
                placeholder={t('savedSearch.savedSearchFilterGrid.searchName')}
                aria-label={t('savedSearch.savedSearchFilterGrid.searchName')}
                value={searchName}
                onChange={handleSearchNameChange}
                onKeyDown={handleKeyEnter}
                dataTestId="name-or-email-input"
                type="search"
                name="search-name"
              />
            </Grid>
            <Grid className="filter-item">
              <Typography
                className="typo-text body3_size"
                data-testid={`saved-search-data-grid-search-idlabel`}
              >
                {t('savedSearch.savedSearchFilterGrid.searchIDLabel')}
              </Typography>
              <ArrTextField
                state="enabled"
                sizeVariant="medium"
                placeholder={t('savedSearch.savedSearchFilterGrid.searchID')}
                aria-label={t('savedSearch.savedSearchFilterGrid.searchID')}
                value={searchID}
                onChange={handleSearchIDChange}
                onKeyDown={handleKeyEnter}
                dataTestId="saved-search-id"
                type="search"
                name="id"
              />
            </Grid>
          </Grid>
          <Box className="filter-action">
            <ArrButton
              aria-label={t('userManagement.manageUsers.applyFilters')}
              variant="primary"
              size="medium"
              onClick={handleApply}
              dataTestId="apply-filter-btn"
              disabled={disableFilters()}
            >
              {t('userManagement.manageUsers.applyFilters')}
            </ArrButton>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              className="arr-divider"
            />
            <ArrIconButton
              icon={<AddFieldSortIcon className="add-field-sort-icon" />}
              onClick={handleManageColumnsClick}
              ariaLabel={t('savedSearch.savedSearchFilterGrid.showHideColumns')}
              shape="default"
              size="medium"
              className="add-filed-sort-icon-btn"
              testId="saved-search-data-grid-show-hide-columns-icon-button"
            />
            <ArrMenu
              id="saved-search-column-menu"
              ref={menuRef2}
              items={items}
              direction="bottom-right"
              className="saved-search-columns"
              keepOpenOnItemClick={true}
              renderItem={(item) => (
                <ColumnMenuItem
                  id={String(item.id)}
                  label={item.label}
                  checked={Boolean(item.checked)}
                  disabled={Boolean(item.disabled)}
                  onToggle={() => item.onToggle?.()}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      <ArrMenu
        id="saved-search-row-menu"
        ref={menuRef1}
        items={savedSearchMoreMenu(roleId, parentId)}
        onItemClick={handleMenuItemClick}
        direction="bottom"
        title={''}
        className="saved-search-row-menu"
      />
      <Box
        className={`grid-main ${isSavedSearchListLoading ? 'grid-loading' : ''}`}
      >
        <ArrDataGrid
          scrollbarSize={0}
          rowHeight={ROW_HEIGHT}
          rows={rows}
          columns={updatedColumns}
          currentPage={effectivePageNumber}
          itemsPerPage={itemsPerPage}
          pageSize={itemsPerPage}
          entityType={'Entities'}
          primaryKey="searchID"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          className="saved-search-grid"
          noRowsOverlay={() => <NoRowsOverlay isFiltered={isFiltered} />}
          columnVisibilityModel={visibility}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          loading={isSavedSearchListLoading}
          showPerPageOptions={true}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          rowCount={savedSearchList?.totalRecords}
          onPageChange={onPageChangeHandler}
          onPaginationClick={(info) =>
            trackPaginationClick(trackGAEvent, info)
          }
          setItemsPerPage={setItemsPerPage}
          disableVirtualization={true}
          disableColumnResize={false}
          getRowClassName={(params) =>
            params.row.searchID === selectedRowId ? 'selected-row' : ''
          }
        />
      </Box>
      <ArrModal
        showModal={openShareModal}
        onClose={() => {
          setShareWith('');
          setOpenShareModal(false);
        }}
        className="saved-search-share-modal"
        title={t('savedSearch.shareModal.title')}
        description={t('savedSearch.shareModal.description')}
        ariaLabel={t('savedSearch.shareModal.title')}
        ariaDescription={t('savedSearch.shareModal.description')}
        dataTestId="saved-search-share-modal"
        gaConfig={{
          name: t('gaTracking.global.shareSavedSearchSubpageName'),
          details: `${t('gaTracking.global.savedSearchGroupMenuDetails', {
            name:
              getSavedSearchRootCategoryName(treeData ?? [], selectedItem) ??
              '',
          })};${t('gaTracking.global.savedSearchIdDetails', {
            id: menuClickedItem.current?.searchID ?? '',
          })}`,
        }}
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setShareWith('');
              setOpenShareModal(false);
            },
            variant: 'secondary',
            dataTestId: 'sss-cancel-btn',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => handleShare(),
            variant: 'primary',
            dataTestId: 'sss-save-btn',
            disabled: !shareWith,
            gaConfig: {
              name: t('gaTracking.global.saveClickName'),
              action: t('gaTracking.global.sharedSavedSearch'),
              details: `${t('gaTracking.global.sharedSavedSearchGroupDetails', {
                name:
                  sharedSecurityGroups?.securityGroups.find(
                    (item) => item.toGroupID.toString() == shareWith
                  )?.name ?? '',
              })};${t('gaTracking.global.savedSearchIdDetails', {
                id: menuClickedItem.current?.searchID ?? '',
              })}`,
            },
          },
        ]}
      >
        <Box className="modal-container">
          <ul data-testid={`saved-search-data-grid-ul`}>
            <li
              className="typo-text body2_size error-text"
              data-testid={`saved-search-data-select-item-li`}
            >
              <ArrRadio
                value={shareWith}
                dataTestId="select-radio-saved-search-grid"
                name={'selectItem'}
                data={
                  sharedSecurityGroups
                    ? sharedSecurityGroups.securityGroups.map((opt) => {
                        return {
                          label: opt.name,
                          value: opt.toGroupID.toString(),
                        };
                      })
                    : []
                }
                onChange={(e) => {
                  setShareWith(e);
                }}
              />
            </li>
          </ul>
        </Box>
      </ArrModal>
      <ArrModal
        showModal={!!renameId}
        onClose={() => {
          setRenameId(0);
          setRenameValue('');
        }}
        className="saved-search-share-modal"
        title={t('savedSearch.renameModal.title')}
        ariaLabel={t('savedSearch.renameModal.title')}
        dataTestId="saved-search-share-modal-1"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setRenameValue('');
              setRenameId(0);
            },
            variant: 'secondary',
            dataTestId: 'sss-cancel-btn-1',
          },
          {
            id: '2',
            label: t('common.save'),
            onClick: () => {
              renameSavedSearch(
                {
                  saveSearchId: renameId,
                  saveSearchName: renameValue,
                },
                {
                  onSuccess: (result) => {
                    triggerToast(result.message, [ToastType.Success]);
                    setRenameValue('');
                    setRenameId(0);
                    updateSavedSearchList();
                  },
                  onError: () => {
                    triggerToast(t('savedSearch.renameModal.renameError'), [
                      ToastType.Error,
                    ]);
                  },
                }
              );
            },
            variant: 'primary',
            dataTestId: 'sss-save-btn-1',
            disabled: !renameValue,
          },
        ]}
      >
        <ArrTextField
          state="enabled"
          sizeVariant="medium"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          dataTestId="saved-search-rename-input"
        />
      </ArrModal>
      <ArrModal
        showModal={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        className="saved-search-share-modal"
        title={t('savedSearch.deleteModal.title')}
        description={t('savedSearch.deleteModal.description')}
        ariaLabel={t('savedSearch.deleteModal.title')}
        ariaDescription={t('savedSearch.deleteModal.description')}
        dataTestId="saved-search-share-modal-2"
        buttons={[
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setOpenDeleteModal(false);
            },
            variant: 'secondary',
            dataTestId: 'ssm2-cancel-btn',
          },
          {
            id: '2',
            label: t('common.ok'),
            onClick: handleDelete,
            variant: 'primary',
            dataTestId: 'ssm2-save-btn',
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
      <SavedSearchDataGridMoveFlyout
        isOpen={openMoveFlyout}
        onClose={() => setOpenMoveFlyout(false)}
        onSave={handleMoveSave}
        savedSearchData={getSavedSearchData()}
      />
    </Box>
  );
};

export default SavedSearchDataGrid;
